import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import ExtensionIcon from "@mui/icons-material/Extension";
import DownloadIcon from "@mui/icons-material/Download";
import { db } from "../../utils/firebase";

const PaywalledPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Extension dialog state
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [extensionDays, setExtensionDays] = useState(7);
  const [extendingAccess, setExtendingAccess] = useState(false);

  useEffect(() => {
    const fetchPaywalledUsers = async () => {
      try {
        setLoading(true);

        // Query users that have temporaryAccessStarted field and order by it in descending order
        // We use a timestamp of 0 to find all entries where the field exists and has a value

        const usersRef = db
          .collection("users")
          .orderBy("temporaryAccessStarted", "desc");

        const snapshot = await usersRef.get();

        // Calculate dates for comparison
        const now = new Date();
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

        // Calculate date for 5 days ago (for the 2-day warning window)
        const fiveDaysAgo = new Date(now);
        fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);

        // Process the results - already ordered by temporaryAccessStarted desc
        const paywalledUsers = snapshot.docs.map((doc) => {
          const data = doc.data();

          // Get the temporaryAccessStarted as a Date object
          const accessDate =
            data.temporaryAccessStarted instanceof Date
              ? data.temporaryAccessStarted
              : data.temporaryAccessStarted.toDate();

          // Check if access was granted MORE than 7 days ago
          const isOlderThanSevenDays = accessDate < sevenDaysAgo;

          // Check if access is about to expire (between 5 and 7 days old)
          const isAboutToExpire =
            accessDate < fiveDaysAgo && accessDate >= sevenDaysAgo;

          // Check if the user has a permanent subscription
          const hasPermanentSubscription = !!data.permanentSubscription;

          // Calculate the remaining days of access
          const accessEndDate = new Date(accessDate);
          accessEndDate.setDate(accessEndDate.getDate() + 7); // Access ends 7 days after it started
          const remainingDays = Math.max(
            0,
            Math.ceil((accessEndDate - now) / (1000 * 60 * 60 * 24))
          );

          return {
            id: doc.id,
            name: data.name || "Unknown",
            email: data.email || "No email",
            temporaryAccessStarted: accessDate,
            temporaryAccessStartedFormatted: accessDate.toLocaleString(),
            isOlderThanSevenDays: isOlderThanSevenDays,
            isAboutToExpire: isAboutToExpire,
            hasPermanentSubscription: hasPermanentSubscription,
            remainingDays: remainingDays,
            // Highlight status:
            // - Red: Expired (>7 days) without permanent subscription
            // - Yellow: About to expire (5-7 days) without permanent subscription
            // - No highlight: Either has permanent subscription or plenty of time left
            highlightStatus: !hasPermanentSubscription
              ? isOlderThanSevenDays
                ? "expired"
                : isAboutToExpire
                ? "expiring"
                : "normal"
              : "normal",
          };
        });

        setUsers(paywalledUsers);
      } catch (err) {
        console.error("Error fetching paywalled users:", err);
        setError("Failed to load paywalled users. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchPaywalledUsers();
  }, []);

  const handleCopyUserId = (userId) => {
    navigator.clipboard
      .writeText(userId)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 3000);
      })
      .catch((err) => {
        console.error("Failed to copy user ID:", err);
      });
  };

  // Handle opening the extension dialog
  const handleOpenExtensionDialog = (user) => {
    setSelectedUser(user);
    setExtensionDialogOpen(true);
  };

  // Handle closing the extension dialog
  const handleCloseExtensionDialog = () => {
    setExtensionDialogOpen(false);
    setSelectedUser(null);
    setExtensionDays(7);
  };

  // Handle extending the trial period
  const handleExtendTrial = async () => {
    if (!selectedUser || extensionDays < 1) return;

    setExtendingAccess(true);

    try {
      // Calculate the new temporaryAccessStarted date
      const now = new Date();

      // If the trial has expired, start a new trial from today
      // If the trial is still active, add days to the existing end date
      let newAccessDate;

      if (selectedUser.isOlderThanSevenDays) {
        // For expired trials, set to now to start a fresh trial period
        newAccessDate = now;
      } else {
        // For active trials, extend by adding days to the current access end date (which is 7 days after it started)
        const currentAccessEndDate = new Date(
          selectedUser.temporaryAccessStarted
        );
        currentAccessEndDate.setDate(currentAccessEndDate.getDate() + 7);

        // Calculate how many days to add after the current end date
        newAccessDate = new Date(currentAccessEndDate);
        newAccessDate.setDate(newAccessDate.getDate() - 7 + extensionDays);
      }

      // Update the user's temporaryAccessStarted field in Firebase
      await db.collection("users").doc(selectedUser.id).update({
        temporaryAccessStarted: newAccessDate,
      });

      // Update local state
      setUsers((prevUsers) =>
        prevUsers.map((user) => {
          if (user.id === selectedUser.id) {
            const now = new Date();
            const sevenDaysAgo = new Date(now);
            sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

            const fiveDaysAgo = new Date(now);
            fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);

            // Calculate new remaining days
            const accessEndDate = new Date(newAccessDate);
            accessEndDate.setDate(accessEndDate.getDate() + 7);
            const remainingDays = Math.max(
              0,
              Math.ceil((accessEndDate - now) / (1000 * 60 * 60 * 24))
            );

            // Check if the new access date is older than seven days
            const isOlderThanSevenDays = newAccessDate < sevenDaysAgo;

            // Check if the new access date is about to expire
            const isAboutToExpire =
              newAccessDate < fiveDaysAgo && newAccessDate >= sevenDaysAgo;

            return {
              ...user,
              temporaryAccessStarted: newAccessDate,
              temporaryAccessStartedFormatted: newAccessDate.toLocaleString(),
              isOlderThanSevenDays: isOlderThanSevenDays,
              isAboutToExpire: isAboutToExpire,
              remainingDays: remainingDays,
              highlightStatus: !user.hasPermanentSubscription
                ? isOlderThanSevenDays
                  ? "expired"
                  : isAboutToExpire
                  ? "expiring"
                  : "normal"
                : "normal",
            };
          }
          return user;
        })
      );

      // Show success notification
      setNotification({
        open: true,
        message: `Successfully extended trial for ${selectedUser.name} by ${extensionDays} days!`,
        severity: "success",
      });

      // Close the dialog
      handleCloseExtensionDialog();
    } catch (err) {
      console.error("Error extending trial period:", err);
      setNotification({
        open: true,
        message: "Failed to extend trial period. Please try again.",
        severity: "error",
      });
    } finally {
      setExtendingAccess(false);
    }
  };

  // Handle closing the notification
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // Get row background color based on highlight status
  const getRowBackgroundColor = (status, isHover = false) => {
    if (status === "expired") return isHover ? "#ef9a9a" : "#ffcdd2"; // Red
    if (status === "expiring") return isHover ? "#fff59d" : "#fff9c4"; // Yellow
    return isHover ? "#f5f5f5" : "inherit"; // Default
  };

  // Get access age chip color
  const getAccessChipColor = (user) => {
    if (user.hasPermanentSubscription) return "success";
    if (user.isOlderThanSevenDays) return "error";
    if (user.isAboutToExpire) return "warning";
    return "success";
  };

  // Convert users data to CSV format
  const convertToCSV = (users) => {
    if (users.length === 0) return "";

    // Define CSV headers
    const headers = [
      "Name",
      "Email",
      "User ID",
      "Temporary Access Started",
      "Remaining Days",
      "Access Status",
      "Permanent Subscription",
    ];

    // Create CSV rows for each user
    const rows = users.map((user) => [
      user.name,
      user.email,
      user.id,
      user.temporaryAccessStartedFormatted,
      user.remainingDays,
      user.hasPermanentSubscription
        ? "Permanent Access"
        : user.isOlderThanSevenDays
        ? "Expired"
        : user.isAboutToExpire
        ? "Expiring Soon"
        : "Active",
      user.hasPermanentSubscription ? "Yes" : "No",
    ]);

    // Convert all values to CSV-friendly format (escape commas, quotes, etc.)
    const processValue = (val) => {
      if (val === null || val === undefined) return "";
      const value = val.toString();
      // If value contains comma, newline or double quote, enclose in double quotes
      const needsQuotes =
        value.includes(",") || value.includes("\n") || value.includes('"');
      if (needsQuotes) {
        // Replace double quotes with two double quotes
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    };

    // Combine headers and rows
    const csvContent = [
      headers.map(processValue).join(","),
      ...rows.map((row) => row.map(processValue).join(",")),
    ].join("\n");

    return csvContent;
  };

  // Handle CSV download
  const handleDownloadCSV = () => {
    try {
      // Convert data to CSV
      const csvContent = convertToCSV(users);

      // Create a Blob with the CSV data
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

      // Create a URL for the Blob
      const url = URL.createObjectURL(blob);

      // Create a temporary link element and trigger download
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `paywalled-users-${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Show success notification
      setNotification({
        open: true,
        message: "CSV file downloaded successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Error downloading CSV:", err);
      setNotification({
        open: true,
        message: "Failed to download CSV. Please try again.",
        severity: "error",
      });
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh"
      >
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <div>
      <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
        Paywalled Users
      </Typography>

      <Box sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: "bold" }}>
            Total Paywalled Users: {users.length}
          </Typography>

          {users.length > 0 && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCSV}
              sx={{ mb: 1 }}
            >
              Download CSV
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#ffcdd2",
                borderRadius: "2px",
                mr: 1,
                border: "1px solid #ef5350",
              }}
            />
            <Typography variant="body2">
              Expired: Temporary access granted more than 7 days ago (without
              permanent subscription)
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              sx={{
                width: 16,
                height: 16,
                bgcolor: "#fff9c4",
                borderRadius: "2px",
                mr: 1,
                border: "1px solid #ffee58",
              }}
            />
            <Typography variant="body2">
              Expiring Soon: Less than 2 days left of temporary access (without
              permanent subscription)
            </Typography>
          </Box>
        </Box>
      </Box>

      {users.length === 0 ? (
        <Typography variant="body1">No paywalled users found</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: "70vh" }}>
          <Table stickyHeader aria-label="paywalled users table">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                >
                  Name
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                >
                  Email
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                >
                  Temporary Access Started
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                >
                  Access Status
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                >
                  Permanent Subscription
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                >
                  User ID
                </TableCell>
                <TableCell
                  sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{
                    backgroundColor: getRowBackgroundColor(
                      user.highlightStatus
                    ),
                    "&:hover": {
                      backgroundColor: getRowBackgroundColor(
                        user.highlightStatus,
                        true
                      ),
                    },
                  }}
                >
                  <TableCell>{user.name}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.temporaryAccessStartedFormatted}</TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        user.isAboutToExpire &&
                        !user.hasPermanentSubscription ? (
                          <WarningIcon />
                        ) : undefined
                      }
                      label={
                        user.hasPermanentSubscription
                          ? "Permanent Access"
                          : user.isOlderThanSevenDays
                          ? "Expired"
                          : user.isAboutToExpire
                          ? `Expires soon (${user.remainingDays} days left)`
                          : `${user.remainingDays} days left`
                      }
                      color={getAccessChipColor(user)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {user.hasPermanentSubscription ? (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CheckCircleIcon
                          color="success"
                          fontSize="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2">Yes</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <CancelIcon
                          color="error"
                          fontSize="small"
                          sx={{ mr: 1 }}
                        />
                        <Typography variant="body2">No</Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {user.id}
                      </Typography>
                      <Tooltip title="Copy User ID">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyUserId(user.id)}
                          sx={{ ml: 1 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<ExtensionIcon />}
                      onClick={() => handleOpenExtensionDialog(user)}
                      disabled={user.hasPermanentSubscription}
                    >
                      Extend Trial
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Extension Dialog */}
      <Dialog
        open={extensionDialogOpen}
        onClose={handleCloseExtensionDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Extend Trial Period for {selectedUser?.name || "User"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {selectedUser?.isOlderThanSevenDays
                ? "This user's trial has expired. Extending will give them a new trial period starting today."
                : `This user's trial ends in ${selectedUser?.remainingDays} days. Extending will add more days to their trial.`}
            </Typography>
            <TextField
              autoFocus
              margin="dense"
              id="days"
              label="Number of Days to Extend"
              type="number"
              fullWidth
              variant="outlined"
              value={extensionDays}
              onChange={(e) =>
                setExtensionDays(Math.max(1, parseInt(e.target.value) || 1))
              }
              InputProps={{ inputProps: { min: 1 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExtensionDialog}>Cancel</Button>
          <Button
            onClick={handleExtendTrial}
            variant="contained"
            color="primary"
            disabled={extendingAccess}
          >
            {extendingAccess ? <CircularProgress size={24} /> : "Extend Trial"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars for notifications */}
      <Snackbar
        open={copySuccess}
        autoHideDuration={3000}
        onClose={() => setCopySuccess(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          User ID copied to clipboard!
        </Alert>
      </Snackbar>

      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default PaywalledPage;
